import { motion } from 'framer-motion'
import { DiamondRow } from '../DiamondRow'
import { stoneKey } from '../../lib/stoneKey'
import type { Diamond } from '../../api/types'

interface Props {
  /** The LATEST assistant response's stones, lifted out of the transcript. */
  results: Diamond[]
  /** Total catalog matches for that search — the count is bigger than the
   *  top slice we render, so the header/footer speak in "best matches" +
   *  "browse all N" rather than mislabelling the slice as the whole set. */
  totalMatches?: number
  /** Hides prices for anonymous visitors (same guard the inline rows use). */
  blurPrice: boolean
  onOpenStone: (s: Diamond) => void
  /** Navigate to the full filtered browse — the SAME action the chat's
   *  "Browse all N matches" pill fires (server-built /search?… with the
   *  conversation's filters). Omitted when the search carried no such action. */
  onBrowseAll?: () => void
}

/**
 * The wide-desktop RIGHT pane on /chat — a frosted `.lqd-pane` sibling to the
 * conversation that surfaces the CURRENT Gemma results as cards, so the empty
 * right gap on wide screens does real work.
 *
 * It reuses the SAME `<DiamondRow/>` the assistant bubble renders inline, so a
 * result reads identically whether it's shown inline (narrow) or lifted here
 * (wide). The parent only mounts this once results exist and only at ≥ lg — on
 * narrower viewports results stay inline in the conversation, untouched.
 *
 * The header used to label the count `results.length` while the list only
 * rendered `slice(0,12)` — so "254 matches" sat over 12 cards. It now says
 * "Showing N best matches · by price" (the honest count of what's shown) and,
 * when the search matched more than we render, offers a "Browse all {total}"
 * button that reuses the chat's own browse-all navigation.
 */
export function ResultsPane({ results, totalMatches, blurPrice, onOpenStone, onBrowseAll }: Props) {
  const shown = results.slice(0, 12)
  // The real catalog total when the server sent one; otherwise fall back to
  // what we hold (never claim more than we can back up).
  const total = typeof totalMatches === 'number' && totalMatches >= results.length
    ? totalMatches
    : results.length
  const hasMore = total > shown.length

  return (
    <motion.aside
      // Subtle enter — slides in from the right as the first results arrive;
      // fades back out when the results clear (new chat / session switch).
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 12 }}
      transition={{ type: 'spring', stiffness: 320, damping: 34 }}
      className="lqd-pane hidden lg:flex w-[20rem] xl:w-[23rem] shrink-0 flex-col rounded-[1.25rem] overflow-hidden"
      data-testid="chat-results-pane"
    >
      {/* Header — the honest count of what's ON SCREEN + the sort tell. */}
      <div className="flex items-baseline justify-between gap-2 px-4 pt-4 pb-3 border-b border-[color:var(--line)]">
        <h2 className="text-[13px] font-semibold tracking-tight text-ink">
          Showing {shown.length} best {shown.length === 1 ? 'match' : 'matches'}
        </h2>
        <span className="lqd-mono text-[10.5px] uppercase tracking-[0.14em] text-soft tabular-nums" data-testid="chat-results-count">
          by price
        </span>
      </div>

      {/* Scrolling card list — reuses DiamondRow (the same card the bubble uses). */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
        {shown.map((s) => (
          <DiamondRow
            key={stoneKey(s)}
            stone={s}
            onOpen={onOpenStone}
            blurPrice={blurPrice}
          />
        ))}
      </div>

      {/* Browse-all — a real button (not a mono footnote) when the search
          matched more than the slice, firing the SAME /search?… navigation as
          the bubble's pill. */}
      {hasMore && onBrowseAll && (
        <div className="border-t border-[color:var(--line)] p-3">
          <button
            type="button"
            onClick={onBrowseAll}
            data-testid="chat-results-browse-all"
            className="lqd-actionpill flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold"
          >
            Browse all {total.toLocaleString()}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
            </svg>
          </button>
        </div>
      )}
    </motion.aside>
  )
}
